import { BrregCompanyData } from '../../companies/hooks/useBrregCompanySearch';
import { CompanyAtAddressItem } from '@client/api';

export type BuildingRole = 'Eier' | 'Investor' | 'Forvalter' | '';

/**
 * A registered Matrikkel owner ("hjemmelshaver") of a building. Juridical owners carry an
 * org number and are imported as Aktørs; physical persons are display-only. Their identity
 * numbers are never carried.
 */
export interface OnboardingBuildingOwner {
  name?: string;
  organizationNumber?: string;
  isJuridical: boolean;
  shareNumerator?: number;
  shareDenominator?: number;
}

export interface OnboardingBuilding {
  id: string;
  name?: string;
  address: string;
  postalCode: string;
  city: string;
  /** ISO-3166 alpha-3 country code; absent for Norway-only search results (backend defaults to NOR). */
  countryThreeLetterCode?: string;
  /** Structured street name/number from manual entry; preferred over re-parsing `address` server-side. */
  streetName?: string;
  streetNumber?: string;
  type: string;
  bra: number;
  selected: boolean;
  role: BuildingRole;
  source?: 'placepoint' | 'manual';
  buildingNumber?: string;
  municipalityCode?: string;
  /** Cadastral land-unit id: Norwegian gnr from search, free-text for manual entry (e.g. Estonian katastritunnus). */
  gnr?: string;
  /** Cadastral property-unit id (Norwegian bnr); free-text for manual entry. */
  bnr?: string;
  numberOfFloors?: number;
  energySourceType?: string;
  heatingType?: string;
  matrikkelBuildingTypeCode?: number;
  latitude?: number;
  longitude?: number;
  ownerOrgNumber?: string;
  ownerName?: string;
  owners?: OnboardingBuildingOwner[];
  /**
   * PlacePoint ownership chain ("Company_OwnerPath") relative to the searched company.
   * Only set when the building was found via org-nr search; persisted for konsern reporting.
   */
  ownerPath?: string;
  /** Org number of the searched company (= root segment of ownerPath). */
  ownerPathRootOrgNumber?: string;
  energyLabel?: string;
  heatingGrade?: string;
  constructionYear?: number;
  energyCertificateUrl?: string;
  energyCertificateNumber?: string;
  energyCertificateIssuedDate?: string;
  isGrouped?: boolean;
  buildingNumbers?: string[];
  childBuildings?: OnboardingBuilding[];
}

export interface OnboardingUser {
  id: string;
  email: string;
  role: 'Administrator' | 'Bruker';
}

/**
 * A company candidate surfaced in the IMPORTER_SELSKAP step.
 * Extends the BRREG snapshot with selection state and the originating building.
 */
export interface ImportCompanyCandidate extends CompanyAtAddressItem {
  sourceBuildingId: string;
  selected: boolean;
}

export interface OnboardingState {
  currentStep: number;
  company: BrregCompanyData | null;
  users: OnboardingUser[];
  buildings: OnboardingBuilding[];
  importCompanies: ImportCompanyCandidate[];
}

export const ONBOARDING_STEPS = [
  { key: 'selskap', label: 'SELSKAP' },
  { key: 'brukere', label: 'BRUKERE' },
  { key: 'bygg', label: 'BYGG' },
  { key: 'roller', label: 'ROLLER' },
  { key: 'importer', label: 'LEIETAKERE' },
  { key: 'gjennomgang', label: 'GJENNOMGANG' },
  { key: 'ferdig', label: 'FERDIG' },
] as const;
